/**
 * PTEL Tool - LoginForm
 * Formulario de inicio de sesión
 *
 * @module components/auth/LoginForm
 * @version 1.4.0
 * @changes
 *   1.4.0 — Elimina texto «Atérminos de uso» (no existe página pública).
 */

import type { FormEvent, CSSProperties } from 'react';
import { useState, useCallback, useRef, useEffect } from 'react';
import { Mail, Lock, Eye, EyeOff, AlertCircle, Loader2, CheckCircle2 } from 'lucide-react';

import type { LoginFormProps, FormErrors, TouchedFields } from './auth.types';
import { VALIDATION_RULES } from './auth.types';
import { getAuthTokens, getButtonStyles, getCardStyles, getAlertStyles } from './auth.tokens';
import { EMAIL_NOT_VERIFIED_ERROR } from '../../contexts/AuthContext';
import { useIsMounted } from '@/hooks/useIsMounted';

type LoginField = 'email' | 'password';

function validateEmail(email: string): string | undefined {
  if (!email.trim()) return VALIDATION_RULES.email.required;
  if (!VALIDATION_RULES.email.pattern.test(email)) return VALIDATION_RULES.email.invalid;
  return undefined;
}
function validatePassword(password: string): string | undefined {
  if (!password) return VALIDATION_RULES.password.required;
  if (password.length < VALIDATION_RULES.password.minLength) return VALIDATION_RULES.password.tooShort;
  return undefined;
}

export function LoginForm({
  theme,
  onSubmit,
  onResendVerification,
  onNavigateToRegister,
  onNavigateToPasswordReset,
  isLoading: externalLoading = false,
  externalError,
  onClearError,
  onClose,
  hideTitle = false,
  hideCard = false,
}: LoginFormProps & { hideTitle?: boolean; hideCard?: boolean }) {
  const t = getAuthTokens(theme);
  const buttonStyles = getButtonStyles(theme);
  const cardStyles = getCardStyles(theme);
  const alertStyles = getAlertStyles(theme, 'error');

  const isMounted = useIsMounted();

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [pwCountdown, setPwCountdown] = useState<number | null>(null);
  const pwTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);

  useEffect(() => () => { if (pwTimerRef.current) clearInterval(pwTimerRef.current); }, []);

  const togglePassword = useCallback(() => {
    if (showPassword) {
      if (pwTimerRef.current) clearInterval(pwTimerRef.current);
      setShowPassword(false);
      setPwCountdown(null);
    } else {
      setShowPassword(true);
      setPwCountdown(5);
      pwTimerRef.current = setInterval(() => {
        setPwCountdown(prev => {
          if (prev === null || prev <= 1) {
            clearInterval(pwTimerRef.current!);
            setShowPassword(false);
            return null;
          }
          return prev - 1;
        });
      }, 1000);
    }
  }, [showPassword]);
  const [errors, setErrors] = useState<FormErrors<LoginField>>({});
  const [touched, setTouched] = useState<TouchedFields<LoginField>>({ email: false, password: false });
  const [focused, setFocused] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const isLoading = externalLoading || isSubmitting;
  const [isResending, setIsResending] = useState(false);
  const [resendSuccess, setResendSuccess] = useState(false);
  const isVerificationError = externalError === EMAIL_NOT_VERIFIED_ERROR;

  const handleResendVerification = useCallback(async () => {
    if (!onResendVerification || !email || !password) return;
    setIsResending(true); setResendSuccess(false);
    try { await onResendVerification(email, password); if (isMounted()) setResendSuccess(true); }
    catch { /* error vía externalError */ }
    finally { if (isMounted()) setIsResending(false); }
  }, [onResendVerification, email, password, isMounted]);

  const handleEmailChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setEmail(e.target.value);
    if (errors.email) setErrors(p => ({ ...p, email: undefined }));
    if (externalError && onClearError) onClearError();
    if (resendSuccess) setResendSuccess(false);
  }, [errors.email, externalError, onClearError, resendSuccess]);

  const handlePasswordChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setPassword(e.target.value);
    if (errors.password) setErrors(p => ({ ...p, password: undefined }));
    if (externalError && onClearError) onClearError();
    if (resendSuccess) setResendSuccess(false);
  }, [errors.password, externalError, onClearError, resendSuccess]);

  const handleBlur = useCallback((field: LoginField) => {
    setTouched(p => ({ ...p, [field]: true }));
    setFocused(null);
    if (field === 'email') setErrors(p => ({ ...p, email: validateEmail(email) }));
    if (field === 'password') setErrors(p => ({ ...p, password: validatePassword(password) }));
  }, [email, password]);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    const emailError = validateEmail(email);
    const passwordError = validatePassword(password);
    setErrors({ email: emailError, password: passwordError });
    setTouched({ email: true, password: true });
    if (emailError || passwordError) return;
    setIsSubmitting(true);
    try { await onSubmit(email, password); } finally { if (isMounted()) setIsSubmitting(false); }
  };

  const inputStyle = (field: string, hasError: boolean): CSSProperties => ({
    width: '100%',
    padding: '10px 12px 10px 40px',
    paddingRight: field === 'password' ? '44px' : '12px',
    background: t.bgInput,
    border: `1px solid ${hasError ? t.borderError : focused === field ? t.borderFocus : t.border}`,
    borderRadius: '8px', color: t.text, fontSize: '14px',
    fontFamily: "'JetBrains Mono', monospace", outline: 'none',
    boxShadow: focused === field ? `0 0 0 3px ${hasError ? t.errorBg : t.focusRing}` : 'none',
    transition: 'border-color 0.15s, box-shadow 0.15s',
  });
  const iconStyle: CSSProperties = {
    position: 'absolute', left: '12px', top: '50%',
    transform: 'translateY(-50%)', color: t.textMuted, pointerEvents: 'none',
  };
  const labelStyle: CSSProperties = {
    display: 'block', marginBottom: '6px', fontSize: '14px',
    fontWeight: 500, color: t.textSecondary, fontFamily: "'Space Grotesk', sans-serif",
  };
  const errStyle: CSSProperties = {
    display: 'flex', alignItems: 'center', gap: '4px',
    marginTop: '4px', fontSize: '12px', color: t.error,
  };

  const inner = (
    <>
      {!hideTitle && (
        <>
          <h2 style={cardStyles.title}>Iniciar sesión</h2>
          <p style={cardStyles.subtitle}>Encuentra, normaliza y geolocaliza infraestructuras municipales</p>
        </>
      )}

      {externalError && !resendSuccess && (
        <div style={alertStyles.container}>
          <AlertCircle size={18} style={alertStyles.icon} />
          <div style={{ flex: 1 }}>
            <span style={alertStyles.text}>{externalError}</span>
            {isVerificationError && onResendVerification && (
              <button type="button" onClick={handleResendVerification} disabled={isResending}
                style={{
                  display: 'block', marginTop: '8px', background: 'none',
                  border: `1px solid ${t.border}`, borderRadius: '6px',
                  padding: '6px 12px', fontSize: '13px', color: t.primary,
                  cursor: isResending ? 'not-allowed' : 'pointer',
                  opacity: isResending ? 0.6 : 1, fontFamily: "'Space Grotesk', sans-serif",
                }}
              >
                {isResending ? 'Enviando…' : 'Reenviar correo de verificación'}
              </button>
            )}
          </div>
        </div>
      )}

      {resendSuccess && (
        <div style={getAlertStyles(theme, 'success').container}>
          <CheckCircle2 size={18} style={{ color: t.success, flexShrink: 0 }} />
          <span style={{ fontSize: '14px', color: t.success }}>
            Correo de verificación reenviado. Revisa tu bandeja de entrada y spam.
          </span>
        </div>
      )}

      <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
        <div>
          <label htmlFor="login-email" style={labelStyle}>Correo electrónico</label>
          <div style={{ position: 'relative' }}>
            <Mail size={18} style={iconStyle} aria-hidden="true" />
            <input id="login-email" type="email" value={email}
              onChange={handleEmailChange} onFocus={() => setFocused('email')}
              onBlur={() => handleBlur('email')} disabled={isLoading}
              placeholder="tu@email.com" autoComplete="email"
              style={inputStyle('email', !!(touched.email && errors.email))}
            />
          </div>
          {touched.email && errors.email && (
            <div style={errStyle}><AlertCircle size={12} /><span>{errors.email}</span></div>
          )}
        </div>

        <div>
          <label htmlFor="login-password" style={labelStyle}>Contraseña</label>
          <div style={{ position: 'relative' }}>
            <Lock size={18} style={iconStyle} aria-hidden="true" />
            <input id="login-password" type={showPassword ? 'text' : 'password'}
              value={password} onChange={handlePasswordChange}
              onFocus={() => setFocused('password')} onBlur={() => handleBlur('password')}
              disabled={isLoading} placeholder="••••••••" autoComplete="current-password"
              style={inputStyle('password', !!(touched.password && errors.password))}
            />
            <button type="button" onClick={togglePassword}
              tabIndex={-1}
              disabled={isLoading}
              style={{ position: 'absolute', right: '12px', top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', padding: '4px', cursor: 'pointer', color: t.textMuted, display: 'flex', alignItems: 'center', gap: '3px' }}
              aria-label={showPassword ? 'Ocultar contraseña' : 'Mostrar contraseña'}
            >
              {showPassword ? <EyeOff size={16} /> : <Eye size={18} />}
              {pwCountdown !== null && (
                <span style={{ fontSize: '10px', fontFamily: "'JetBrains Mono', monospace", lineHeight: 1, minWidth: '8px' }}>
                  {pwCountdown}
                </span>
              )}
            </button>
          </div>
          {touched.password && errors.password && (
            <div style={errStyle}><AlertCircle size={12} /><span>{errors.password}</span></div>
          )}
        </div>

        {onNavigateToPasswordReset && (
          <div style={{ textAlign: 'right', marginTop: '-12px' }}>
            <button type="button" onClick={onNavigateToPasswordReset}
              disabled={isLoading} style={cardStyles.link}>
              ¿Olvidaste tu contraseña?
            </button>
          </div>
        )}

        <button type="submit" disabled={isLoading}
          style={{ ...buttonStyles.base, ...(isLoading ? buttonStyles.disabled : {}) }}>
          {isLoading
            ? <><Loader2 size={18} className="animate-spin" /><span>Iniciando sesión...</span></>
            : <span>Iniciar sesión</span>}
        </button>
      </form>

      {!hideTitle && onNavigateToRegister && (
        <>
          <div style={cardStyles.divider}>
            <div style={cardStyles.dividerLine} />
            <span style={cardStyles.dividerText}>o</span>
            <div style={cardStyles.dividerLine} />
          </div>
          <p style={{ ...cardStyles.footer, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '24px' }}>
            <span>¿No tienes cuenta?</span>
            <button type="button" onClick={onNavigateToRegister} disabled={isLoading} style={cardStyles.link}>
              Crear cuenta
            </button>
          </p>
        </>
      )}

      <style>{`
        @keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
        .animate-spin { animation: spin 1s linear infinite; }
      `}</style>
    </>
  );

  if (hideCard) return <>{inner}</>;

  return (
    <div style={{ width: '100%', maxWidth: '440px', margin: '0 auto' }}>
      <div style={{ ...cardStyles.container, position: 'relative' }}>
        {onClose && (
          <button type="button" onClick={onClose} aria-label="Cerrar" style={{
            position: 'absolute', top: '12px', right: '12px', zIndex: 10,
            width: '32px', height: '32px', background: 'transparent',
            border: `1px solid ${t.border}`, borderRadius: '50%',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            color: t.textSecondary, cursor: 'pointer', padding: 0,
          }}><span style={{ fontSize: '16px', lineHeight: 1 }}>×</span></button>
        )}
        {inner}
      </div>
    </div>
  );
}

export default LoginForm;
